import { Button } from '@steelwake/ui';
import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';

import { api, ApiError, describeError } from '../api.js';
import { safeNext } from './RequireAuth.js';
import { useAppStore } from '../store.js';
import type { UserDto } from '../types.js';
import { Card, Field, Notice, PageTitle } from '../ui.js';

export function Login() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const setUser = useAppStore((s) => s.setUser);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [needsTotp, setNeedsTotp] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(
    params.get('oauth') === 'error'
      ? `Sign-in with a provider failed (${params.get('reason') ?? 'unknown'}).`
      : null,
  );

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await api<{ user: UserDto }>('/api/v1/auth/login', {
        method: 'POST',
        body: { email, password, ...(needsTotp && totpCode ? { totpCode } : {}) },
      });
      setUser(res.user);
      await navigate(safeNext(params.toString()) ?? '/account');
    } catch (err) {
      if (err instanceof ApiError && err.code === 'TOTP_REQUIRED') {
        setNeedsTotp(true);
        setError('Enter the 6-digit code from your authenticator app.');
      } else {
        setError(describeError(err));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <PageTitle>Log in</PageTitle>
      <form onSubmit={submit} noValidate>
        <Field
          label="Email"
          name="email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <Field
          label="Password"
          name="password"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        {needsTotp ? (
          <Field
            label="Authenticator code"
            name="totpCode"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={totpCode}
            onChange={(e) => setTotpCode(e.target.value)}
          />
        ) : null}
        {error ? (
          <Notice kind="error" testId="form-error">
            {error}
          </Notice>
        ) : null}
        <Button type="submit" disabled={busy}>
          {busy ? 'Signing in…' : 'Log in'}
        </Button>
      </form>
      <p>
        <Link to="/forgot-password">Forgot password?</Link> ·{' '}
        <Link to="/register">Create an account</Link>
      </p>
    </Card>
  );
}
